<template>
  <!-- Design: ProfilePanel. Opened by any 'bv::toggle::collapse' with 'applicant-sidebar' (result
       list, notebook, shared ?workerViewId= link). The profile itself stays the job server's CV
       page: its API (/cv/{id}) serves only some partners. -->
  <div v-if="open" class="ts-profile-panel-root">
    <div class="offcanvas-backdrop fade show ts-jobad-backdrop" aria-hidden="true" @click="close"></div>
    <aside
      ref="panel"
      class="offcanvas offcanvas-end ts-jobad ts-profile-panel show"
      role="dialog"
      aria-modal="true"
      aria-labelledby="tsProfilePanelTitle"
      tabindex="-1"
      @keydown.esc.stop="close"
    >
      <ts-panel-header
        :previous-label="text.previousProfile"
        :next-label="text.nextProfile"
        :previous-disabled="listIndex <= 0"
        :next-disabled="listIndex < 0 || listIndex >= applicantListIds.length - 1"
        @close="close"
        @previous="goTo(listIndex - 1)"
        @next="goTo(listIndex + 1)"
      />

      <!-- Design: Bewerber:innenprofil | Direkt kontaktieren in a tab row under the bar, like the job
           panel. The contact tab is active while the CV page shows its contact form. -->
      <div class="ts-job-ad-tabrow d-flex align-items-center border-bottom">
        <nav class="ts-job-ad-tabs flex-grow-1" :aria-label="translations[13867]">
          <div class="nav nav-tabs border-0">
            <button
              id="tsProfilePanelTitle"
              class="nav-link"
              :class="{ active: !cvContactOpen }"
              type="button"
              :aria-pressed="String(!cvContactOpen)"
              @click="showCvProfile"
            >
              <i class="bi bi-person-vcard" aria-hidden="true"></i>{{ translations[13867] }}
            </button>
            <button
              v-if="isActionsAllowed"
              class="nav-link"
              :class="{ active: cvContactOpen }"
              type="button"
              :aria-pressed="String(cvContactOpen)"
              @click="contact"
            >
              <i class="bi bi-envelope" aria-hidden="true"></i>{{ translations[3575] }}
            </button>
          </div>
        </nav>
      </div>

      <div class="offcanvas-body p-0">
        <div class="row g-0 h-100">
          <aside class="col-12 col-lg-4 ts-profile-summary">
            <div class="ts-panel-summary-inner p-4">
              <div class="ts-profile-avatar d-flex align-items-center justify-content-center mx-auto overflow-hidden" :class="{ 'has-photo': cvPhoto }">
                <img v-if="cvPhoto" class="ts-profile-avatar-photo" :src="cvPhoto" :alt="cvPhotoAlt" />
                <i v-else class="bi bi-person" aria-hidden="true"></i>
              </div>
              <div class="ts-panel-info-id mt-4 pt-3">
                <i class="bi bi-hash ts-panel-info-icon" aria-hidden="true"></i>
                <strong class="ts-panel-info-text">{{ text.applicantIdLabel }}: {{ applicantId }}</strong>
                <button class="btn ts-copy-button" type="button" :aria-label="text.copyApplicantId" :title="text.copyApplicantId" @click="copyApplicantId">
                  <i class="bi" :class="copied ? 'bi-check-lg' : 'bi-copy'" aria-hidden="true"></i>
                </button>
                <!-- The short link /workers/<id> (sections/shareLinks.ts) -->
                <button class="btn ts-copy-button" type="button" :aria-label="copyLinkText" :title="copyLinkText" @click="copyLink">
                  <i class="bi" :class="linkCopied ? 'bi-check-lg' : 'bi-link-45deg'" aria-hidden="true"></i>
                </button>
              </div>

              <!-- The CV as DOCX / PDF, taken over from the CV page: for consultants only -->
              <div v-if="isConsultant && cvDownloads.length" class="mt-4">
                <div class="fw-semibold small mb-2">{{ cvDownloadLabel }}</div>
                <div class="d-flex gap-2">
                  <a
                    v-for="download in cvDownloads"
                    :key="download.type"
                    class="btn btn-outline-dark flex-fill"
                    :href="download.href"
                    target="_blank"
                    rel="noopener"
                  >
                    <i class="bi me-1" :class="download.icon" aria-hidden="true"></i>{{ download.label }}
                  </a>
                </div>
              </div>

              <!-- Contact and notebook for companies and consultants; guests get the CV page's contact
                   form when the partner allows contact without login, else they are asked to log in -->
              <template v-if="isActionsAllowed">
                <button class="btn btn-primary w-100 mt-4" type="button" @click="contact">{{ translations[3575] }}</button>
                <button class="btn btn-dark w-100 mt-2" type="button" :aria-pressed="String(isSaved)" @click="toggleNotepad">
                  <i class="bi me-2" :class="isSaved ? 'bi-bookmark-check-fill' : 'bi-bookmark'" aria-hidden="true"></i>
                  {{ isSaved ? translations[10983] : translations[505] }}
                </button>
              </template>

              <template v-if="row">
                <hr class="my-4" />
                <div class="ts-panel-info-list">
                  <div v-if="row.position" class="ts-panel-info-row">
                    <i class="bi bi-person-badge ts-panel-info-icon" aria-hidden="true"></i>
                    <strong class="ts-panel-info-text">{{ decode(row.position) }}<template v-if="row.age"> ({{ row.age }})</template></strong>
                  </div>
                  <div v-if="row.locations" class="ts-panel-info-row">
                    <i class="bi bi-geo-alt ts-panel-info-icon" aria-hidden="true"></i>
                    <span class="ts-panel-info-text">{{ row.locations }}</span>
                  </div>
                  <div v-if="languages" class="ts-panel-info-row">
                    <i class="bi bi-translate ts-panel-info-icon" aria-hidden="true"></i>
                    <span class="ts-panel-info-text">{{ languages }}</span>
                  </div>
                </div>
              </template>

              <!-- Google translation of the CV (no print here: profiles are not printed) -->
              <template v-if="cvLanguages.length">
                <hr class="my-4" />
                <div class="ts-job-eyebrow mb-2">{{ text.moreActions }}</div>
                <div class="ts-panel-info-list">
                  <ts-panel-language
                    :languages="cvLanguages"
                    :prompt="cvLanguagePrompt"
                    :value="cvLanguage"
                    @select="translateCv"
                  />
                </div>
              </template>
            </div>
          </aside>
          <article class="col-12 col-lg-8 ts-profile-content ts-profile-frame-wrap position-relative p-0">
            <div v-if="iframeLoading" class="ts-loading d-flex align-items-center justify-content-center" role="status">
              <span class="spinner-border" aria-hidden="true"></span>
              <span class="visually-hidden">{{ text.loading }}</span>
            </div>
            <iframe
              id="job-iframe"
              ref="cvFrame"
              :key="cvUrl"
              class="ts-job-ad-frame"
              :title="`${translations[13867]} ${applicantId}`"
              :src="cvUrl"
              allowfullscreen
              @load="onFrameLoad"
            ></iframe>
          </article>
        </div>
      </div>
    </aside>
    <worker-contact-modal />
  </div>
</template>

<script lang="ts">
import { Component, Vue, Watch } from 'vue-property-decorator';
import { AppModule, AuthTab } from '@/store/modules/app';
import { copyLinkLabel, copyText, shareLink } from '@/sections/shareLinks';
import { WorkersModule } from '@/store/modules/workers';
import { UserModule } from '@/store/modules/user';
import { NotebookModule } from '@/store/modules/notebook';
import { logEvent } from '@/api/EventLogger';
import { Applicant } from '@/api/Workers';
import { EVENT_APPLICANT_PROFILE_VIEW } from '@/config/constant';
import { groupCurses } from '@/config/configuration';
import TsPanelHeader from '@/components/Talentspace/detail/TsPanelHeader.vue';
import WorkerContactModal from '@/components/Workers/WorkerContactModal.vue';
import TsPanelLanguage from '@/components/Talentspace/detail/TsPanelLanguage.vue';
import {
  addFrameStyle, FrameLanguage, frameDocumentOf, readFrameLanguages, selectFrameLanguage,
} from '@/sections/frameBar';
import { restyleCvContactForm, restyleCvPage } from '@/sections/workers/cvFrame';

// The CV page through this portal's own /jobnet (production) so its bar can be read and hidden
// Fluid layout for the job server's CV page (cv.css has fixed 968px columns)
const CV_FLUID_CSS = [
  'body { overflow-x: hidden; }',
  '.container_prof { width: auto !important; max-width: 968px; padding: 0 24px; box-sizing: border-box; }',
  '.main_info, .big_line_2, .qual, .left_block, .right_block { width: auto !important; max-width: 100%; }',
  '.main_info { float: none !important; }',
  '.photo_block { display: none !important; }',
  '.date { width: 25% !important; }',
  '.info { width: 72% !important; }',
  '.left_block, .right_block { width: 48% !important; }',
  'img { max-width: 100%; height: auto; }',
].join('\n');

const CV_SERVER = process.env.NODE_ENV === 'production' ? '/jobnet' : 'https://job-server.net/jobnet';

const CV_CONTACT_LINK = '.footer_menu a[href*="contact_form.php"]';

type ListApplicant = Applicant & { age?: number, languages?: { language_name: string, level: string }[] };

@Component({
  components: { TsPanelHeader, WorkerContactModal, TsPanelLanguage },
})
export default class TsProfilePanel extends Vue {
  open = false;

  iframeLoading = true;

  // Google translation languages of the CV page's own bar (the bar itself is hidden)
  cvLanguages: FrameLanguage[] = [];

  cvLanguagePrompt = '';

  cvLanguage = '';

  // Watches the CV page for a translation (it drops our styles)
  cvObserver: MutationObserver | null = null;

  // The applicant's photo from the CV page (shown here instead of there); '' = none / placeholder
  cvPhoto = '';

  cvPhotoAlt = '';

  // DOCX / PDF links of the CV page and its "Herunterladen:" label
  cvDownloads: { type: string, label: string, icon: string, href: string }[] = [];

  cvDownloadLabel = '';

  // "Direkt kontaktieren" of the CV page's bar: only there when the partner lets guests contact
  hasCvContactForm = false;

  // The CV page shows its contact form (or the message after sending it) instead of the CV
  cvContactOpen = false;

  copied = false;

  linkCopied = false;

  // Focus goes back here when the panel closes
  opener: HTMLElement | null = null;

  get translations() {
    return AppModule.translation;
  }

  get text() {
    return {
      previousProfile: AppModule.translation[16570],
      nextProfile: AppModule.translation[16571],
      applicantIdLabel: AppModule.translation[202],
      copyApplicantId: AppModule.translation[16572],
      moreActions: AppModule.translation[5402],
      loading: AppModule.translation[16555],
    };
  }

  get isAuth() {
    return UserModule.isUserAuth;
  }

  get isConsultant(): boolean {
    return Boolean(UserModule.accessTokenConsultant);
  }

  get languageSid() {
    return AppModule.languageSid;
  }

  get applicantId(): number | null {
    return WorkersModule.selectedWorkerId;
  }

  get applicantListIds(): number[] {
    return WorkersModule.workersList.map((value: Applicant) => value.id);
  }

  get listIndex(): number {
    return this.applicantId === null ? -1 : this.applicantListIds.indexOf(this.applicantId);
  }

  // The row of the result list, when the profile came from there
  get row(): ListApplicant | null {
    return (WorkersModule.workersList.find((value: Applicant) => value.id === this.applicantId) as ListApplicant) || null;
  }

  get languages(): string {
    return (this.row?.languages || []).map((language) => `${language.language_name} ${language.level}`).join(', ');
  }

  get token() {
    return UserModule.accessTokenApplicant || UserModule.accessTokenConsultant || UserModule.accessTokenCompany || '';
  }

  get shareApplicantLink() {
    return window.btoa(`${window.location.origin}/workers?lang=${this.$route.query.lang}&workerViewId=${this.applicantId}`);
  }

  get cvUrl(): string {
    return `${CV_SERVER}/cv/${this.applicantId}?sid=${this.languageSid}&portal=1&token=${this.token}&share=${this.shareApplicantLink}`;
  }

  // Guests (they are asked to log in), companies and consultants — not applicants
  get isActionsAllowed() {
    return !this.isAuth || Boolean(UserModule.accessTokenCompany || UserModule.accessTokenConsultant);
  }

  get isSaved(): boolean {
    return NotebookModule.notebookWorkers.some((x: { id: number }) => x.id === this.applicantId);
  }

  decode(text = ''): string {
    if (!text.includes('&')) return text;
    const field = document.createElement('textarea');
    field.innerHTML = text;
    return field.value;
  }

  // ─── open / close ────────────────────────────────────────────────────────────

  onToggle(id: string) {
    if (id !== 'applicant-sidebar') return;
    if (this.open) this.close();
    else this.show();
  }

  show() {
    this.opener = document.activeElement as HTMLElement | null;
    this.open = true;
    this.iframeLoading = true;
    document.body.classList.add('ts-jobad-open');
    window.addEventListener('message', this.handleMessage, false);
    this.logApplicantView();
    this.$nextTick(() => (this.$refs.panel as HTMLElement | undefined)?.focus());
  }

  close() {
    this.open = false;
    document.body.classList.remove('ts-jobad-open');
    window.removeEventListener('message', this.handleMessage, false);
    this.opener?.focus?.();
  }

  @Watch('applicantId')
  onApplicantChange() {
    if (!this.open) return;
    this.iframeLoading = true;
    this.cvPhoto = '';
    this.cvDownloads = [];
    this.logApplicantView();
  }

  goTo(index: number) {
    const id = this.applicantListIds[index];
    if (id !== undefined) WorkersModule.SET_SELECTED_WORKER(id);
  }

  // ─── actions ─────────────────────────────────────────────────────────────────

  contact() {
    // Already open: a second click would load an empty form over what was typed
    if (this.cvContactOpen) return;
    if (!this.isAuth && this.hasCvContactForm) {
      this.openCvContactForm();
      return;
    }
    if (!this.isAuth) {
      this.$bvModal.show('not-auth-modal');
      return;
    }
    if (this.applicantId) WorkersModule.SET_SELECTED_APPLICANT_CONTACT(this.applicantId);
    this.$bvModal.show('modal-contact-profile');
  }

  // Companies and consultants on the server; guests keep it in the browser (as the result card)
  async toggleNotepad() {
    if (!this.applicantId) return;
    if (this.isAuth) {
      await NotebookModule.toogleNotepadItem({ type: 'applicant', item_id: this.applicantId, isMarked: this.isSaved });
      return;
    }
    if (!groupCurses(AppModule.sid)) this.$bvModal.show('not-auth-modal');
    const a = this.row;
    NotebookModule.setWorkerNotepadItemLocal({
      id: this.applicantId,
      isMarked: this.isSaved,
      position: a?.position || '',
      position_keywords: a?.position_keywords || '',
      locations: a?.locations || '',
      professions: a?.professions || '',
      availability: a?.availability || '',
      last_modified: a?.last_modified || '',
      reg_date: a?.reg_date || '',
      salary_hour: a?.salary_hour || 0,
      salary_year: a?.salary_year || 0,
    });
  }

  // The CV page prints itself when it is ours; otherwise it opens to be printed
  onFrameLoad() {
    this.iframeLoading = false;
    const page = frameDocumentOf(this.$refs.cvFrame as HTMLIFrameElement | undefined);
    const { prompt, languages } = readFrameLanguages(page?.querySelector('#id_translation_select') || null);
    this.cvLanguagePrompt = prompt;
    this.cvLanguages = languages;
    this.cvLanguage = '';
    this.readCvPhoto(page);
    this.readCvDownloads(page);
    this.styleCvPage(page);
    this.hasCvContactForm = Boolean(page?.querySelector(CV_CONTACT_LINK));
    this.watchCvPage(page);
  }

  styleCvPage(page: Document | null) {
    // Its notebook, contact and print buttons are in the left panel already
    addFrameStyle(page, 'ts-hide-bar', '.footer_menu { display: none !important; }');
    // The CV page is laid out for 968px: let it follow the frame width. Its photo, ID and
    // downloads column is in the left panel now.
    if (page?.querySelector('.container_prof')) addFrameStyle(page, 'ts-fluid-cv', CV_FLUID_CSS);
    if (this.cvPhoto) addFrameStyle(page, 'ts-hide-photo', '.photo_block .photo { display: none !important; }');
    restyleCvPage(page);
    restyleCvContactForm(page, this.showCvProfile);
    this.cvContactOpen = Boolean(page?.querySelector('.main-content-block') && !page.querySelector('.container_prof'));
  }

  // A translation (/jobnet/translator) swaps the page's whole <head> and content, so our
  // styles and tags are gone: put them back whenever the page changes
  watchCvPage(page: Document | null) {
    this.cvObserver?.disconnect();
    this.cvObserver = null;
    if (!page) return;
    const observer = new MutationObserver(() => this.styleCvPage(page));
    observer.observe(page.documentElement, { childList: true, subtree: true });
    this.cvObserver = observer;
  }

  // Back from the contact form: the CV page again
  showCvProfile() {
    if (!this.cvContactOpen) return;
    const frame = this.$refs.cvFrame as HTMLIFrameElement | undefined;
    this.iframeLoading = true;
    frame?.contentWindow?.location.reload();
  }

  // The CV page loads its contact form in place of the CV (its own link, as on the old portal)
  openCvContactForm() {
    const frame = this.$refs.cvFrame as HTMLIFrameElement | undefined;
    const link = frameDocumentOf(frame)?.querySelector(CV_CONTACT_LINK) as HTMLElement | null;
    if (!link) return;
    link.click();
    frame?.contentWindow?.scrollTo(0, 0);
  }

  readCvDownloads(page: Document | null) {
    const links = Array.from(page?.querySelectorAll('.photo_block #icons a') || []) as HTMLAnchorElement[];
    this.cvDownloads = links
      .map((link) => {
        const type = /type=pdf/i.test(link.href) ? 'pdf' : 'docx';
        return {
          type,
          label: type.toUpperCase(),
          icon: type === 'pdf' ? 'bi-file-earmark-pdf' : 'bi-file-earmark-word',
          href: link.href,
        };
      });
    this.cvDownloadLabel = (page?.querySelector('.photo_block .download_label')?.textContent || '').trim();
  }

  // The CV page shows a placeholder picture when there is no photo; that one stays out
  readCvPhoto(page: Document | null) {
    const photo = page?.querySelector('.photo_block .photo img') as HTMLImageElement | null;
    const hasPhoto = Boolean(photo?.src) && !/platzhalter/i.test(photo?.src || '');
    this.cvPhoto = hasPhoto && photo ? photo.src : '';
    this.cvPhotoAlt = hasPhoto && photo ? photo.alt : '';
  }

  // The page's own select loads the translation (/jobnet/translator) into the CV
  translateCv(value: string) {
    const page = frameDocumentOf(this.$refs.cvFrame as HTMLIFrameElement | undefined);
    selectFrameLanguage(page?.querySelector('#id_translation_select') || null, value);
    this.cvLanguage = value;
  }

  get copyLinkText() {
    return copyLinkLabel();
  }

  async copyLink() {
    await copyText(shareLink('workers', this.applicantId));
    this.linkCopied = true;
    window.setTimeout(() => { this.linkCopied = false; }, 1500);
  }

  async copyApplicantId() {
    const value = String(this.applicantId);
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      const field = document.createElement('textarea');
      field.value = value;
      field.setAttribute('readonly', '');
      field.style.position = 'fixed';
      field.style.opacity = '0';
      document.body.append(field);
      field.select();
      document.execCommand('copy');
      field.remove();
    }
    this.copied = true;
    window.setTimeout(() => { this.copied = false; }, 1500);
  }

  // Messages of the CV page: log in (as a company), note the profile
  async handleMessage(event: MessageEvent) {
    switch (event.data?.message) {
      case 'login_required':
        this.$bvModal.show('not-auth-modal');
        break;
      case 'login_required_company':
        AppModule.SET_SELECTED_AUTH_TAB(AuthTab.firm);
        this.$bvModal.show('login-modal');
        break;
      case 'note_applicant':
        if (UserModule.accessTokenCompany || UserModule.accessTokenConsultant) {
          await NotebookModule.toogleNotepadItem({ type: 'applicant', item_id: event.data.id, isMarked: event.data.saved });
        }
        break;
      default:
        break;
    }
  }

  async logApplicantView() {
    if (!this.isAuth || !this.applicantId) return;
    await logEvent({ event_id: EVENT_APPLICANT_PROFILE_VIEW, object_id: this.applicantId });
  }

  created() {
    this.$root.$on('bv::toggle::collapse', this.onToggle);
  }

  beforeDestroy() {
    this.$root.$off('bv::toggle::collapse', this.onToggle);
    window.removeEventListener('message', this.handleMessage, false);
    document.body.classList.remove('ts-jobad-open');
    this.cvObserver?.disconnect();
  }
}
</script>
